import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  Firestore,
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, Functions, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, FirebaseStorage, getStorage } from 'firebase/storage';
import { getEnvVars } from '../utils/env';
import { createLogger } from '../utils/logger';
import { getAppCheckToken, initializeFirebaseAppCheck } from './appCheck';

const logger = createLogger('FirebaseConfig');

/**
 * Firebase configuration interface
 */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * Firebase initialization options
 */
export interface FirebaseOptions {
  config: FirebaseConfig;
  useEmulators?: boolean;
  environment?: 'development' | 'staging' | 'production';
  emulatorConfig?: {
    authUrl?: string;
    firestoreHost?: string;
    firestorePort?: number;
    storageHost?: string;
    storagePort?: number;
  };
  /** See {@link FirestoreCacheMode}. Defaults to `persistent`. */
  firestoreCache?: FirestoreCacheMode;
}




/**
 * Assembles the Firebase config object based on environment.
 * Firebase configurations are hardcoded for each environment.
 *
 * Environment detection:
 * - APP_ENV determines which Firebase project (dev/staging/prod)
 * - USE_EMULATOR determines if we connect to local emulators
 *
 * Configurations:
 * - 'production': Uses letsorderup-prod Firebase project
 * - 'staging': Uses letsorderup-staging Firebase project
 * - 'development':
 *   - With USE_EMULATOR === 'true': Connects to local emulators
 *   - With USE_EMULATOR !== 'true': Uses letsorderup-dev Firebase project
 */
const getConfig = () => {
  const envVars = getEnvVars();
  const framework = typeof window !== 'undefined' ? 'Browser' : 'Server';

  // Determine environment (matching server-side logic)
  const firebaseEnv = envVars.APP_ENV || 'development';

  logger.debug('Detected environment', { framework, firebaseEnv });

  // Handle each environment
  switch (firebaseEnv) {
    case 'production': {
      logger.info('Using PRODUCTION Firebase config (letsorderup-prod)');
      return {
        config: {
          apiKey: "AIzaSyBeKQ9VLtOHCBM2MKc9_wLojoCPkM6bE7c",
          authDomain: "google.identity.letsorderup.com",
          projectId: "letsorderup-prod",
          storageBucket: "letsorderup-prod.firebasestorage.app",
          messagingSenderId: "1086508509422",
          appId: "1:1086508509422:web:dfcd397eb49389671eebff",
          measurementId: "G-T4X2RR5FDS"
        },
        useEmulators: false
      };
    }

    case 'staging': {
      logger.info('Using STAGING Firebase config (letsorderup-staging)');
      return {
        config: {
          apiKey: "AIzaSyARff-3rcVDxDH_XksWO2ihik7SF-rsP-U",
          authDomain: "google.staging.letsorderup.com",
          projectId: "letsorderup-staging",
          storageBucket: "letsorderup-staging.firebasestorage.app",
          messagingSenderId: "425185583524",
          appId: "1:425185583524:web:8531b3fb0dc996a47be131",
          measurementId: "G-XHTRN3ZPLQ"
        },
        useEmulators: false
      };
    }

    default: { // 'development' or any other case
      // Check if we should use emulators (matching server-side logic)
      const useEmulator = envVars.USE_EMULATOR === 'true';

      if (useEmulator) {
        logger.debug('Using DEVELOPMENT (Emulators)');

        // Dynamic emulator host for cross-device compatibility
        const emulatorHost = (envVars.ENABLE_CROSS_DEVICE === 'true' && envVars.DEV_HOST)
          ? envVars.DEV_HOST
          : '127.0.0.1';

        logger.debug('Emulator host', { emulatorHost });

        return {
          config: {
            apiKey: "AIzaSyAftE2dJwmf4uFQFp8Lex_fSXLfrdCvZcM",
            authDomain: "letsorderup-dev.firebaseapp.com",
            projectId: "letsorderup-dev",
            storageBucket: "letsorderup-dev.firebasestorage.app",
            messagingSenderId: "725928457443",
            appId: "1:725928457443:web:c9e9927cae5773ed5401d6",
            measurementId: "G-RSNWHXMZHJ"
          },
          useEmulators: true,
          emulatorHost
        };
      } else {
        logger.info('Using DEVELOPMENT Firebase config (letsorderup-dev)');
        return {
          config: {
            apiKey: "AIzaSyAftE2dJwmf4uFQFp8Lex_fSXLfrdCvZcM",
            authDomain: "google.auth.letsorderup.dev",
            projectId: "letsorderup-dev",
            storageBucket: "letsorderup-dev.firebasestorage.app",
            messagingSenderId: "725928457443",
            appId: "1:725928457443:web:c9e9927cae5773ed5401d6",
            measurementId: "G-RSNWHXMZHJ"
          },
          useEmulators: false
        };
      }
    }
  }
};

let app: FirebaseApp;

/**
 * True for a WebKit browser: Safari on the Mac, and every browser on iPhone /
 * iPad (all of them are WebKit underneath, whatever their name).
 *
 * WebKit's IndexedDB is where the persistent multi-tab cache breaks: a guest
 * with the site open in a second tab (or a stale lease from an earlier visit)
 * leaves the cart write queued behind a primary-tab lease that never comes
 * back, and the write promise never settles — checkout spun on "Starting
 * checkout…" on iPhone and Mac Safari (production 2026-10-10) while Chrome and
 * a Safari private window, neither of which shares that state, checked out.
 */
export const isWebKitBrowser = (userAgent: string, maxTouchPoints = 0): boolean =>
  /iP(hone|ad|od)/.test(userAgent) ||
  // iPadOS reports a desktop Mac user agent; a touch screen gives it away.
  (/Macintosh/.test(userAgent) && maxTouchPoints > 1) ||
  (/Safari\//.test(userAgent) && !/Chrom|Edg\/|OPR\/|Firefox\/|Android/.test(userAgent));

/**
 * Where the Firestore client keeps the documents it has read.
 *
 * - `persistent` — IndexedDB, so reloads and new tabs serve the working set
 *   from disk and reconcile in the background (customer apps: menus, carts).
 * - `memory` — nothing is written to disk; the cache dies with the tab.
 *
 * The BACK-OFFICE apps (management, admin, billing, crm) pass `memory`
 * (speed plan 2026-10-17 §6.4, SOC 2). A persistent cache writes EVERY document
 * the browser reads to disk and nothing ever clears it — on a shared office PC
 * that left employees' private comp/PII records, read by the management
 * employee list, on disk for the next person at the keyboard. Firebase's own
 * guidance is not to enable persistence for data that must not outlive the
 * session. `appProviderFirestoreCache.test.ts` holds each back-office root to
 * `memory`.
 */
export type FirestoreCacheMode = 'persistent' | 'memory';

export interface InitializeOptions {
  /** Defaults to `persistent` (unchanged for the customer-facing apps). */
  readonly firestoreCache?: FirestoreCacheMode;
}

/**
 * The IndexedDB databases the Firestore SDK created for this project under the
 * default app. Firestore names them `firestore/[DEFAULT]/<projectId>/…`.
 */
export const firestorePersistenceDbPrefix = (projectId: string): string =>
  `firestore/[DEFAULT]/${projectId}/`;

/**
 * A back-office app that used to run with the persistent cache still has the
 * old cache on disk. Delete it once, in the background, so switching to the
 * memory cache actually removes what earlier sessions wrote. A delete blocked by
 * another open tab (still on an older build) completes when that tab closes.
 * Never throws — a failed purge must not stop the app booting.
 */
export const purgePersistedFirestoreCache = async (projectId: string | undefined): Promise<void> => {
  if (!projectId || typeof indexedDB === 'undefined') return;
  const prefix = firestorePersistenceDbPrefix(projectId);
  try {
    const names: string[] = [];
    if (typeof indexedDB.databases === 'function') {
      for (const db of await indexedDB.databases()) {
        if (db.name && db.name.startsWith(prefix)) names.push(db.name);
      }
    } else {
      // No enumeration (older Firefox): the default database's well-known name.
      names.push(`${prefix}main`);
    }
    for (const name of names) {
      indexedDB.deleteDatabase(name);
    }
    if (names.length > 0) {
      logger.info('Removed the persisted Firestore cache (back-office memory mode)', {
        databases: names.length,
      });
    }
  } catch (error) {
    logger.warn('Could not remove the persisted Firestore cache', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * Configure the Firestore instance's local cache. MUST run exactly once,
 * immediately after `initializeApp` and BEFORE any `getFirestore(app)` call
 * (Firestore settings are locked once the instance is first accessed).
 *
 * `persistent`: an IndexedDB cache with the multi-tab manager, so reloads / new
 * tabs serve the working set from disk instead of re-reading it all (latency +
 * read cost). WebKit gets the in-memory cache instead — see
 * {@link isWebKitBrowser}. A write there is acknowledged or rejected by the
 * server, never parked behind another tab's IndexedDB lease.
 *
 * `memory`: always the in-memory cache, and any cache an earlier build left on
 * disk is deleted ({@link purgePersistedFirestoreCache}).
 */
export const configureFirestoreCache = (firebaseApp: FirebaseApp, mode: FirestoreCacheMode): void => {
  // No IndexedDB on the Next.js server (SSR) or in some sandboxed contexts →
  // let `getFirestore` lazily create the default in-memory instance.
  const hasIndexedDb = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
  if (!hasIndexedDb) return;

  const useMemory =
    mode === 'memory' || isWebKitBrowser(navigator.userAgent, navigator.maxTouchPoints);

  try {
    initializeFirestore(firebaseApp, {
      localCache: useMemory
        ? memoryLocalCache()
        : persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch (error) {
    // Private-browsing / unsupported / already-initialized → fall back to an
    // explicit in-memory cache so the app still boots.
    logger.warn('Persistent Firestore cache unavailable; using in-memory cache', {
      error: error instanceof Error ? error.message : String(error),
    });
    try {
      initializeFirestore(firebaseApp, { localCache: memoryLocalCache() });
    } catch {
      // Instance already created elsewhere — default (memory) cache applies.
    }
  }

  if (mode === 'memory') {
    void purgePersistedFirestoreCache(firebaseApp.options.projectId);
  }
};

/**
 * Initialize Firebase automatically based on environment detection
 * @returns Initialized Firebase app
 */
export const initialize = (options: InitializeOptions = {}): FirebaseApp => {
  if (getApps().length > 0) {
    return getApp();
  }

  const { config, useEmulators, emulatorHost } = getConfig();

  app = initializeApp(config);
  configureFirestoreCache(app, options.firestoreCache ?? 'persistent');

  logger.info('Firebase App initialized', { projectId: app.options.projectId });

  if (useEmulators) {
    const host = emulatorHost || '127.0.0.1';
    logger.debug('Connecting to Firebase Emulators', { host });
    connectAuthEmulator(getAuth(app), `http://${host}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(app), host, 8080);
    connectFunctionsEmulator(getFunctions(app), host, 5001);
    connectStorageEmulator(getStorage(app), host, 9199);
  }

  // Initialize App Check (skipped automatically for emulators or missing site key).
  // When initialization succeeds, fire-and-forget a token fetch so the
  // reCAPTCHA Enterprise challenge happens during idle bundle-parse time
  // rather than blocking the first API call (typically /auth/signin).
  // isTokenAutoRefreshEnabled keeps it warm afterward.
  const appCheckInstance = initializeFirebaseAppCheck(app, { useEmulators });
  if (appCheckInstance) {
    void getAppCheckToken().catch(() => {
      // Best-effort warmup — appCheck.ts already logs failures.
    });
  }

  return app;
};

/**
 * Initialize Firebase with explicit configuration (for advanced use cases)
 * @param options Firebase configuration and options
 * @returns Initialized Firebase app
 */
export const initializeWithOptions = (options: FirebaseOptions): FirebaseApp => {
  if (getApps().length > 0) {
    return getApp();
  }

  const { config, useEmulators = false, environment = 'production', emulatorConfig } = options;

  app = initializeApp(config);
  configureFirestoreCache(app, options.firestoreCache ?? 'persistent');

  logger.info('Firebase App initialized', { projectId: app.options.projectId, environment });

  if (useEmulators) {
    logger.debug('Connecting to Firebase Emulators');

    const authUrl = emulatorConfig?.authUrl || 'http://127.0.0.1:9099';
    const firestoreHost = emulatorConfig?.firestoreHost || '127.0.0.1';
    const firestorePort = emulatorConfig?.firestorePort || 8080;
    const functionsHost = emulatorConfig?.firestoreHost || '127.0.0.1';
    const functionsPort = 5001;
    const storageHost = emulatorConfig?.storageHost || '127.0.0.1';
    const storagePort = emulatorConfig?.storagePort || 9199;

    connectAuthEmulator(getAuth(app), authUrl, { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(app), firestoreHost, firestorePort);
    connectFunctionsEmulator(getFunctions(app), functionsHost, functionsPort);
    connectStorageEmulator(getStorage(app), storageHost, storagePort);
  }

  // Initialize App Check (skipped automatically for emulators or missing site key).
  // Same warmup as `initialize()` above — pull the first reCAPTCHA Enterprise
  // challenge off the critical path.
  const appCheckInstance = initializeFirebaseAppCheck(app, { useEmulators });
  if (appCheckInstance) {
    void getAppCheckToken().catch(() => {
      // Best-effort warmup — appCheck.ts already logs failures.
    });
  }

  return app;
};

const getFirebaseApp = (): FirebaseApp => {
  if (!app) {
    throw new Error(
      'Firebase has not been initialized. Call initialize() first.',
    );
  }
  return app;
};

export const getFirebaseAuth = (): Auth => getAuth(getFirebaseApp());
export const getFirebaseFirestore = (): Firestore => getFirestore(getFirebaseApp());
export const getFirebaseFunctions = (): Functions => getFunctions(getFirebaseApp());
export const getFirebaseStorage = (): FirebaseStorage => getStorage(getFirebaseApp());
